/**
 * Transformations API.
 *
 * The error contract for these endpoints has not yet been decided by the
 * backend. For now, callers should treat any failure as "model broken,
 * disable actions" (per the Transformations API design doc) and check the
 * result with `isArdoqError` from `@ardoq/common-helpers` — no typed error
 * body is exposed.
 */
import type {
  APIReferenceAttributes,
  ArdoqId,
  APITransformationsConfigurationUpdateResponse,
  APICreateFutureStateComponentTypeRequest,
  APICreateFutureStateComponentTypeResponse,
  CreateFutureStateClaimGroupRequest,
  CreateFutureStateClaimsRequest,
  CreateFutureStateClaimsResponse,
  CreateFutureStateRequest,
  CreateInitiativeRequest,
  ExecuteFutureStateAnalysisRequest,
  ExecuteFutureStateAnalysisResponse,
  FutureState,
  FutureStateApprovalRequest,
  FutureStateClaimGroup,
  FutureStateClaimGroupsResponse,
  RemoveFutureStateClaimGroupRequest,
  UpdateFutureStateClaimGroupRequest,
  FutureStateClaimsWriteResponse,
  FutureStateClaimsResponse,
  FutureStatePageResponse,
  FutureStateUpdatedResponse,
  Initiative,
  InitiativePageResponse,
  InitiativeUpdateResponse,
  InitiativesView,
  OwnerSuggestion,
  FutureStateCompletionResponse,
  FutureStateRenderRequest,
  RenderResponse,
  SelectionComponentsResponse,
  SelectionIdsResponse,
  SetTargetFutureStateClaimsRequest,
  TransformationsConfiguration,
  TransformationsConfigurationUpdateResponse,
  TransformationsConfigurationValidation,
  TransformationsConfigurationWithNames,
  UpdateFutureStateClaimsRequest,
  UpdateFutureStateRequest,
  UpdateInitiativeRequest,
} from '@ardoq/api-types';
import {
  fetchJson,
  fetchVoidAndRespondWithId,
  Method,
  toRequestOptions,
} from './api';
import { ApiResponse } from './types';
import { isArdoqError, Result } from '@ardoq/common-helpers';

const fetchInitiatives = (): ApiResponse<InitiativesView> => {
  const request = new Request('/api/transformations/initiatives');
  return fetchJson<InitiativesView>(request);
};

const fetchInitiative = (
  initiativeId: ArdoqId
): ApiResponse<InitiativePageResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}`
  );
  return fetchJson<InitiativePageResponse>(request);
};

const createInitiative = (
  body: CreateInitiativeRequest
): ApiResponse<Initiative> => {
  const request = new Request(
    '/api/transformations/initiatives/create',
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<Initiative>(request);
};

const updateInitiative = ({
  initiativeId,
  body,
}: {
  initiativeId: ArdoqId;
  body: UpdateInitiativeRequest;
}): ApiResponse<InitiativeUpdateResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/update`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<InitiativeUpdateResponse>(request);
};

const deleteInitiative = (initiativeId: ArdoqId): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoidAndRespondWithId(request, initiativeId);
};

const fetchFutureState = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<FutureStatePageResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}`
  );
  return fetchJson<FutureStatePageResponse>(request);
};

const createFutureState = (
  initiativeId: ArdoqId,
  body: CreateFutureStateRequest
): ApiResponse<FutureState> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/create`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureState>(request);
};

const updateFutureState = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: UpdateFutureStateRequest;
}): ApiResponse<FutureStateUpdatedResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/update`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateUpdatedResponse>(request);
};

const unapproveFutureState = ({
  initiativeId,
  futureStateId,
  description,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
} & FutureStateApprovalRequest): ApiResponse<FutureState> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/unapprove`,
    toRequestOptions(Method.POST, { description })
  );
  return fetchJson<FutureState>(request);
};

const approveFutureState = ({
  initiativeId,
  futureStateId,
  description,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
} & FutureStateApprovalRequest): ApiResponse<FutureState> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/approve`,
    toRequestOptions(Method.POST, { description })
  );
  return fetchJson<FutureState>(request);
};

const deleteFutureState = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<ArdoqId> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoidAndRespondWithId(request, futureStateId);
};

const fetchOwnerSuggestions = ({
  q,
  limit,
}: {
  q: string;
  limit?: number;
}): ApiResponse<OwnerSuggestion[]> => {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set('limit', String(limit));
  return fetchJson<OwnerSuggestion[]>(
    new Request(`/api/transformations/owners/suggestions?${params}`)
  );
};

const fetchAssigneeSuggestions = ({
  q,
  limit,
}: {
  q: string;
  limit?: number;
}): ApiResponse<OwnerSuggestion[]> => {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set('limit', String(limit));
  return fetchJson<OwnerSuggestion[]>(
    new Request(`/api/transformations/assignees/suggestions?${params}`)
  );
};

const fetchFutureStateClaims = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<FutureStateClaimsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claims`
  );
  return fetchJson<FutureStateClaimsResponse>(request);
};

const createFutureStateClaims = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: CreateFutureStateClaimsRequest;
}): ApiResponse<CreateFutureStateClaimsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claims/create`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<CreateFutureStateClaimsResponse>(request);
};

const updateFutureStateClaims = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: UpdateFutureStateClaimsRequest;
}): ApiResponse<FutureStateClaimsWriteResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claims/update`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateClaimsWriteResponse>(request);
};

const setTargetFutureStateClaims = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: SetTargetFutureStateClaimsRequest;
}): ApiResponse<FutureStateClaimsWriteResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claims/set-target`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateClaimsWriteResponse>(request);
};

const removeFutureStateClaims = ({
  initiativeId,
  futureStateId,
  ids,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  ids: ArdoqId[];
}): ApiResponse<FutureStateClaimsWriteResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claims/remove`,
    toRequestOptions(Method.POST, { ids })
  );
  return fetchJson<FutureStateClaimsWriteResponse>(request);
};

const mergeFutureStateClaims = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<FutureStateClaimsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/merge`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson<FutureStateClaimsResponse>(request);
};

const createFutureStateClaimGroup = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: CreateFutureStateClaimGroupRequest;
}): ApiResponse<FutureStateClaimGroup> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claim-groups/create`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateClaimGroup>(request);
};

const updateFutureStateClaimGroup = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: UpdateFutureStateClaimGroupRequest;
}): ApiResponse<FutureStateClaimGroup> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claim-groups/update`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateClaimGroup>(request);
};

const removeFutureStateClaimGroup = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: RemoveFutureStateClaimGroupRequest;
}): ApiResponse<FutureStateClaimGroupsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/claim-groups/remove`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<FutureStateClaimGroupsResponse>(request);
};

const fetchFutureStateSelection = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<SelectionComponentsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/selection`
  );
  return fetchJson<SelectionComponentsResponse>(request);
};

const setFutureStateSelection = ({
  initiativeId,
  futureStateId,
  ids,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  ids: ArdoqId[];
}): ApiResponse<SelectionIdsResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/selection`,
    toRequestOptions(Method.POST, { ids })
  );
  return fetchJson<SelectionIdsResponse>(request);
};

const renderFutureState = ({
  initiativeId,
  futureStateId,
  body,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  body: FutureStateRenderRequest;
}): ApiResponse<RenderResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/render`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<RenderResponse>(request);
};

const fetchFutureStateCompletion = ({
  initiativeId,
  futureStateId,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
}): ApiResponse<FutureStateCompletionResponse> => {
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/completion`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson<FutureStateCompletionResponse>(request);
};

const executeAnalysis = ({
  initiativeId,
  futureStateId,
  clientRequestId,
  claimIds,
}: {
  initiativeId: ArdoqId;
  futureStateId: ArdoqId;
  clientRequestId: string;
  claimIds: ArdoqId[];
}): ApiResponse<ExecuteFutureStateAnalysisResponse> => {
  const body: ExecuteFutureStateAnalysisRequest = { claimIds };
  const request = new Request(
    `/api/transformations/initiatives/${initiativeId}/future-states/${futureStateId}/execute-analysis?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<ExecuteFutureStateAnalysisResponse>(request);
};

const referencesBetween = (
  componentIds: ArdoqId[]
): ApiResponse<APIReferenceAttributes[]> => {
  const request = new Request(
    '/api/transformations/references-between',
    toRequestOptions(Method.POST, { 'component-ids': componentIds })
  );
  return fetchJson<{ references: APIReferenceAttributes[] }>(request).then(
    (result): Result<APIReferenceAttributes[]> =>
      isArdoqError(result) ? result : result.references
  );
};

const toSingleConfiguration = (
  configurations: TransformationsConfiguration[]
): TransformationsConfiguration => configurations[0] ?? {};

const toUpdateResponse = ({
  configurations,
  validation,
}: APITransformationsConfigurationUpdateResponse): TransformationsConfigurationUpdateResponse => ({
  configuration: toSingleConfiguration(configurations),
  validation,
});

const fetchConfiguration = (): ApiResponse<TransformationsConfiguration> =>
  fetchJson<TransformationsConfiguration[]>(
    new Request('/api/transformations/configuration')
  ).then((result): Result<TransformationsConfiguration> =>
    isArdoqError(result) ? result : toSingleConfiguration(result)
  );

const fetchConfigWithNames =
  (): ApiResponse<TransformationsConfigurationWithNames> =>
    fetchJson<TransformationsConfigurationWithNames>(
      new Request('/api/transformations/config')
    );

const validateConfiguration = (
  body: TransformationsConfiguration
): ApiResponse<TransformationsConfigurationValidation> => {
  const request = new Request(
    '/api/transformations/configuration/validate',
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<TransformationsConfigurationValidation>(request);
};

const updateConfiguration = (
  body: TransformationsConfiguration
): ApiResponse<TransformationsConfigurationUpdateResponse> => {
  const request = new Request(
    '/api/transformations/configuration/update',
    toRequestOptions(Method.POST, [body])
  );
  return fetchJson<APITransformationsConfigurationUpdateResponse>(request).then(
    (result): Result<TransformationsConfigurationUpdateResponse> =>
      isArdoqError(result) ? result : toUpdateResponse(result)
  );
};

const createFutureStateComponentType = (
  body: APICreateFutureStateComponentTypeRequest
): ApiResponse<APICreateFutureStateComponentTypeResponse> => {
  const request = new Request(
    '/api/transformations/configuration/create-future-state-component-type',
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<APICreateFutureStateComponentTypeResponse>(request);
};

export const transformationsApi = {
  fetchInitiatives,
  fetchInitiative,
  createInitiative,
  updateInitiative,
  deleteInitiative,
  fetchFutureState,
  createFutureState,
  updateFutureState,
  approveFutureState,
  unapproveFutureState,
  deleteFutureState,
  fetchOwnerSuggestions,
  fetchAssigneeSuggestions,
  fetchFutureStateClaims,
  createFutureStateClaims,
  updateFutureStateClaims,
  setTargetFutureStateClaims,
  removeFutureStateClaims,
  mergeFutureStateClaims,
  createFutureStateClaimGroup,
  updateFutureStateClaimGroup,
  removeFutureStateClaimGroup,
  fetchFutureStateSelection,
  setFutureStateSelection,
  renderFutureState,
  fetchFutureStateCompletion,
  referencesBetween,
  executeAnalysis,
  fetchConfiguration,
  fetchConfigWithNames,
  validateConfiguration,
  updateConfiguration,
  createFutureStateComponentType,
};
